import { cfg } from 'config/runtimeConfig'
/**
 * Transport for the Kit19 REST API (Kit19API_V2).
 *
 * Every request posts { "Token": 12345, "Details": { ... } } and unwraps the
 * standard { "Status": 1, "Message": "Success", "Details": ... } envelope.
 * There is no WCF/.asmx transport — every legacy WebMethod the entity screens
 * relied on now has a REST route (see endpoints.ts).
 */

export const API_BASE_URL: string = (
  cfg('REACT_APP_API_BASE_URL') ?? '/api'
).replace(/\/+$/, '')

export const API_TOKEN: string = cfg('REACT_APP_API_TOKEN') ?? '-2295521862261168'

export const USE_MOCK: boolean = String(cfg('REACT_APP_USE_MOCK') ?? 'false') === 'true'

/**
 * Session identity carried by the (encrypted) query string.
 *
 * The legacy pages read UserId / ParentId / ModuleCode / TimeZone from hidden
 * fields the master page rendered for the logged-in session. Here the link
 * itself carries them — `resolveEntityParams` decrypts the query string and
 * stores whatever session keys it finds; `.env` values are only the fallback.
 */
export interface SessionOverrides {
  userId?: number
  parentId?: number
  moduleCode?: string
  timeZone?: string
  /** Overrides VITE_API_TOKEN for every request when the link carries a token. */
  token?: string
}

let sessionOverrides: SessionOverrides = {}

export function setSessionOverrides(overrides: SessionOverrides): void {
  sessionOverrides = { ...sessionOverrides, ...overrides }
}

export function getSessionOverrides(): SessionOverrides {
  return sessionOverrides
}

/**
 * The token business requests carry: a numeric token from the (encrypted)
 * link, else the universal .env token.
 *
 * NEVER the SSO JWT — same architecture as the Kit19Sales module: the JWT is
 * only for auth.kit19.com (ValidateToken/Logout). Business endpoints validate
 * the numeric Kit19 token, and the SubscriptionValidationHandler bypasses its
 * plan check only for the universal token; sending the JWT here makes every
 * call fail with "Invalid Token" or a -99 subscription block. Identity travels
 * as UserId/ParentId inside Details instead.
 */
export function currentToken(): string {
  const override = sessionOverrides.token
  // Kit19 API tokens are numeric — refuse a JWT (or anything else) here.
  if (override && override !== '' && !Number.isNaN(Number(override))) return override
  return API_TOKEN
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly route?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface PostOptions {
  signal?: AbortSignal
}

/**
 * Calls a REST endpoint with the standard { Token, Details } wrapper and
 * unwraps the response envelope.
 */
export async function apiPost<TResult>(
  route: string,
  details: unknown = {},
  options: PostOptions = {},
): Promise<TResult> {
  const isAlreadyWrapped =
    typeof details === 'object' &&
    details !== null &&
    ('Token' in details || 'Details' in details)

  const payload = isAlreadyWrapped ? details : { Token: coerceToken(currentToken()), Details: details }

  return postJson<TResult>(route, payload, options)
}

/**
 * Posts a body verbatim — no { Token, Details } wrapper. Needed for the few
 * routes that bind a raw JObject, e.g. IMS/Whatsapp/Template, which takes the
 * exact payload the legacy jsInvWA built.
 */
export async function apiPostRaw<TResult>(
  route: string,
  body: unknown,
  options: PostOptions = {},
): Promise<TResult> {
  return postJson<TResult>(route, body, options)
}

async function postJson<TResult>(route: string, payload: unknown, options: PostOptions): Promise<TResult> {
  const url = `${API_BASE_URL}/${route.replace(/^\/+/, '')}`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
      signal: options.signal,
    })
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    throw new ApiError(`Network error calling ${route}: ${(err as Error).message}`, undefined, route)
  }

  const text = await response.text()
  let body: any = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      // A non-JSON body on a failed request is an error page (e.g. the IIS 404
      // for a route that is not deployed) — never hand that back as data.
      if (!response.ok) {
        throw new ApiError(
          `${response.status} ${response.statusText} from ${route} (non-JSON response)`,
          response.status,
          route,
        )
      }
      return text as unknown as TResult
    }
  }

  if (!response.ok) {
    // The API reports handled failures as HTTP 417 with the envelope intact —
    // surface the server's message rather than the bare status line.
    const message =
      body && typeof body === 'object' && 'Message' in body && body.Message
        ? `${body.Message} (${route})`
        : `${response.status} ${response.statusText} from ${route}`
    throw new ApiError(message, response.status, route)
  }

  if (!body) return undefined as TResult

  // Unwrap REST envelope { Status, Message, Details }
  if (typeof body === 'object' && 'Status' in body) {
    if (body.Status !== 1 && body.Status !== true && body.Status !== 200) {
      const message =
        body.Status === -1
          ? `Invalid API token (${route}). Check VITE_API_TOKEN.`
          : body.Message || `Request to ${route} failed.`
      throw new ApiError(message, body.Status, route)
    }
    // `Details: null` is a legitimate empty result (e.g. a lead with no notes).
    // Only fall back to the envelope when the key is absent entirely, otherwise
    // callers receive the whole { Status, Message, ... } object and blow up on
    // `.map`.
    return unwrapDetails<TResult>('Details' in body ? body.Details : body)
  }

  // Direct payload (e.g. IMS/Whatsapp/Template relays the messenger response)
  return unwrapDetails<TResult>(body)
}

function unwrapDetails<T>(details: unknown): T {
  if (typeof details === 'string') {
    const trimmed = details.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.parse(trimmed) as T
      } catch {
        return details as T
      }
    }
  }
  return details as T
}

function coerceToken(token: string): string | number {
  if (token !== '' && !Number.isNaN(Number(token))) return Number(token)
  return token
}
